import React, { useEffect, useRef } from 'react';
import { ThemeToggle } from '../ui/ThemeToggle';

/**
 * Publishes the sticky bar's own height as --app-nav-height.
 *
 * input.css turns that into scroll-padding-top on <html>, so everything the
 * browser scrolls into view — anchors, scrollIntoView, focus — clears the bar
 * on its own. The bar is the only thing that knows how tall it is, and a
 * ResizeObserver keeps the value true when it wraps or the viewport changes.
 */
function usePublishedNavHeight() {
  const ref = useRef(null);

  useEffect(() => {
    const el = ref.current;
    if (!el) return undefined;

    const publish = () => {
      document.documentElement.style.setProperty('--app-nav-height', `${el.offsetHeight}px`);
    };

    publish();
    const observer = new ResizeObserver(publish);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  return ref;
}

// Navigation Button Component
export function NavButton({ label, isActive, onClick, icon }) {
  return (
    <button
      onClick={onClick}
      className={`
        flex items-center space-x-2 px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200
        ${isActive 
          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300 shadow-xs' 
          : 'text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800/50 hover:text-slate-900 dark:hover:text-slate-100'
        }
        transform hover:scale-105 active:scale-95
      `}
    >
      {icon}
      <span>{label}</span>
    </button>
  );
}

// Mobile Navigation Item Component
export function MobileNavItem({ label, isActive, onClick }) {
  return (
    <button
      onClick={onClick}
      className={`
        w-full text-left px-4 py-3 rounded-lg text-sm font-medium transition-all duration-200
        ${isActive 
          ? 'bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-300' 
          : 'text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800'
        }
      `}
    >
      {label}
    </button>
  );
}

// Main Navigation Bar Component
export function NavigationBar({ currentPage, navigateTo, user, handleLogout, theme, setTheme, sidebarOpen, setSidebarOpen }) {
  const navRef = usePublishedNavHeight();

  return (
    <>
      {/* Modern Navigation Bar */}
      <nav ref={navRef} className="bg-white/80 dark:bg-slate-900/80 backdrop-blur-lg border-b border-slate-200/50 dark:border-slate-700/50 sticky top-0 z-50 transition-all duration-300">
        <div className="w-full px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            {/* Logo and Brand */}
            <div className="flex items-center space-x-4">
              <button 
                onClick={() => navigateTo('dashboard')}
                className="min-w-0 flex items-center group transition-all duration-200 hover:scale-105 focus:outline-none rounded-xl p-1"
              >
                <div className="min-w-0">
                  <h1 className="text-lg sm:text-xl font-bold brand-rainbow transition-all duration-200 truncate">
                    HR-Dashboard
                  </h1>
                  <p className="hidden sm:block text-xs text-slate-500 dark:text-slate-400 group-hover:text-slate-600 dark:group-hover:text-slate-300 transition-colors duration-200">Recruitment Management</p>
                </div>
              </button>
            </div>

            {/* Navigation Links */}
            <div className="hidden md:flex items-center space-x-1">
              <NavButton 
                label="Dashboard" 
                isActive={currentPage.page === 'dashboard'} 
                onClick={() => navigateTo('dashboard')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 7v10a2 2 0 002 2h14a2 2 0 002-2V9a2 2 0 00-2-2H5a2 2 0 00-2-2z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 5v0" /></svg>}
              />
              <NavButton 
                label="Fragebögen" 
                isActive={currentPage.page === 'questionnaire-editor'} 
                onClick={() => navigateTo('questionnaire-editor')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" /></svg>}
              />
              <NavButton 
                label="Ergebnisse" 
                isActive={currentPage.page === 'results'} 
                onClick={() => navigateTo('results')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" /></svg>}
              />
              <NavButton 
                label="Bewerbungspool" 
                isActive={currentPage.page === 'bewerbungspool'} 
                onClick={() => navigateTo('bewerbungspool')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 21V5a2 2 0 00-2-2H7a2 2 0 00-2 2v16m14 0h2m-2 0h-5m-9 0H3m2 0h5M9 7h1m-1 4h1m4-4h1m-1 4h1m-5 10v-5a1 1 0 011-1h2a1 1 0 011 1v5m-4 0h4" /></svg>}
              />
              <NavButton
                label="Stellenanzeigen"
                isActive={currentPage.page === 'stellenanzeigen'}
                onClick={() => navigateTo('stellenanzeigen')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 13.255A23.931 23.931 0 0112 15c-3.183 0-6.22-.62-9-1.745M16 6V5a2 2 0 00-2-2h-4a2 2 0 00-2 2v1m4 6h.01M5 20h14a2 2 0 002-2V8a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" /></svg>}
              />
              <NavButton
                label="Statistiken"
                isActive={currentPage.page === 'statistiken'}
                onClick={() => navigateTo('statistiken')}
                icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" /></svg>}
              />
            </div>

            {/* Right side items */}
            <div className="flex items-center space-x-3">
              {/* Settings — anchored right */}
              <div className="hidden md:block">
                <NavButton
                  label="Einstellungen"
                  isActive={currentPage.page === 'settings'}
                  onClick={() => navigateTo('settings')}
                  icon={<svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24" aria-hidden="true" role="img"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" /><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" /></svg>}
                />
              </div>

              {/* User info and logout */}
              {user && (
                <div className="flex items-center space-x-2 px-3 py-1 rounded-full bg-slate-100 dark:bg-slate-800 border border-slate-200 dark:border-slate-700">
                  <span className="text-sm text-slate-700 dark:text-slate-300">
                    {user.username || user.name || 'Benutzer'}
                  </span>
                  <button
                    onClick={handleLogout}
                    className="p-1 rounded-full text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                    title="Abmelden"
                  >
                    <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                    </svg>
                  </button>
                </div>
              )}
              
              <ThemeToggle theme={theme} setTheme={setTheme} />
              
              {/* Mobile menu button */}
              <button 
                onClick={() => setSidebarOpen(!sidebarOpen)}
                className="md:hidden p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
              >
                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      </nav>

      {/* Mobile Navigation Sidebar */}
      {sidebarOpen && (
        <>
          <div className="fixed inset-0 z-40 bg-black bg-opacity-50 md:hidden" onClick={() => setSidebarOpen(false)} />
          <div className="fixed top-0 right-0 bottom-0 w-64 bg-white dark:bg-slate-900 z-50 md:hidden transform transition-transform duration-300 ease-in-out">
            <div className="p-6">
              <div className="flex justify-between items-center mb-8">
                <h2 className="text-lg font-semibold text-slate-800 dark:text-slate-100">Navigation</h2>
                <button onClick={() => setSidebarOpen(false)} className="p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800">
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  </svg>
                </button>
              </div>
              <nav className="space-y-2">
                <MobileNavItem label="Dashboard" isActive={currentPage.page === 'dashboard'} onClick={() => { navigateTo('dashboard'); setSidebarOpen(false); }} />
                <MobileNavItem label="Fragebögen" isActive={currentPage.page === 'questionnaire-editor'} onClick={() => { navigateTo('questionnaire-editor'); setSidebarOpen(false); }} />
                <MobileNavItem label="Ergebnisse" isActive={currentPage.page === 'results'} onClick={() => { navigateTo('results'); setSidebarOpen(false); }} />
                <MobileNavItem label="Bewerbungspool" isActive={currentPage.page === 'bewerbungspool'} onClick={() => { navigateTo('bewerbungspool'); setSidebarOpen(false); }} />
                <MobileNavItem label="Stellenanzeigen" isActive={currentPage.page === 'stellenanzeigen'} onClick={() => { navigateTo('stellenanzeigen'); setSidebarOpen(false); }} />
                <MobileNavItem label="Statistiken" isActive={currentPage.page === 'statistiken'} onClick={() => { navigateTo('statistiken'); setSidebarOpen(false); }} />
                <MobileNavItem label="Einstellungen" isActive={currentPage.page === 'settings'} onClick={() => { navigateTo('settings'); setSidebarOpen(false); }} />
                
                {/* Logout button */}
                {user && (
                  <div className="pt-4 border-t border-slate-200 dark:border-slate-700">
                    <div className="px-3 py-2 text-sm text-slate-600 dark:text-slate-400">
                      Angemeldet als: {user.username || user.name || 'Benutzer'}
                    </div>
                    <button
                      onClick={() => { handleLogout(); setSidebarOpen(false); }}
                      className="w-full flex items-center space-x-2 px-3 py-2 text-sm font-medium text-red-600 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 hover:bg-red-50 dark:hover:bg-red-900/20 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1" />
                      </svg>
                      <span>Abmelden</span>
                    </button>
                  </div>
                )}
              </nav>
            </div>
          </div>
        </>
      )}
    </>
  );
}
